import { createAppKit } from '@reown/appkit/react'
import { WagmiProvider } from 'wagmi'
import { bsc } from '@reown/appkit/networks' // Changed from arbitrum, mainnet
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi'

// Setup queryClient
const queryClient = new QueryClient()

// Project ID from cloud.reown.com
const projectId = '6545183a7108cb76a00ce1ac4e1a5e11'

// Determine the current environment
const isLocalhost = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const isDevEnvironment = typeof window !== 'undefined' && window.location.hostname === 'dev.bitsport.gg';

// Set the base URL based on the environment
const baseUrl = isLocalhost
  ? 'http://localhost:5173'
  : (isDevEnvironment ? 'https://dev.bitsport.gg' : 'https://app.bitsport.gg');

// Metadata object
const metadata = {
  name: 'BitSport',
  description: 'Where AI Meets Web3 Gaming: Play, Earn, Predict',
  url: baseUrl,
  icons: [`${baseUrl}/logo.png`]
}

// Log the AppKit configuration for debugging
console.log('AppKit Configuration:');
console.log('Environment:', isLocalhost ? 'Localhost' : (isDevEnvironment ? 'Development' : 'Production'));
console.log('Hostname:', typeof window !== 'undefined' ? window.location.hostname : 'unknown');
console.log('Base URL:', baseUrl);

// Set the networks
const networks = [bsc] // Changed from mainnet, arbitrum

// Function to check if Passport is being used
const isUsingPassport = () => {
  if (typeof window === 'undefined') return false;

  const walletProvider = localStorage.getItem('wallet_provider');
  const passportAddress = localStorage.getItem('passport_address');
  const passportUserInfo = localStorage.getItem('passport_user_info');

  return walletProvider === 'passport' ||
         (passportAddress && passportUserInfo);
};

// Create Wagmi Adapter with enhanced configuration for persistence
export const wagmiAdapter = new WagmiAdapter({
  networks,
  projectId,
  ssr: false,
  walletConnectProjectId: projectId,
  showBalance: true,
  showAddress: true,
  autoConnect: !isUsingPassport(), // Only auto-connect if not using Passport
  persistence: {
    enabled: true,
    storage: 'localStorage'
  }
})

// Create AppKit instance with all required functionality
const appKitInstance = createAppKit({
  adapters: [wagmiAdapter],
  networks,
  projectId,
  metadata,
  features: {
    analytics: true,
    persistence: {
      enabled: true,
      storage: 'localStorage'
    },
    signatureCache: true,
    connection: {
      autoConnect: !isUsingPassport(), // Only auto-connect if not using Passport
      reconnect: !isUsingPassport(), // Only reconnect if not using Passport
      persistentSessions: true,
      sessionExpiry: 14 * 24 * 60 * 60 * 1000, // 14 days in milliseconds
      reconnectOnRefresh: !isUsingPassport() // Only reconnect on refresh if not using Passport
    },
    balance: {
      enabled: true,
      networks: [bsc],
      tokens: ['native']
    }
  },
  sessionPersistence: {
    enabled: true,
    persistSignatures: true,
    expiry: 14 * 24 * 60 * 60 * 1000 // 14 days in milliseconds
  }
})

// Export the AppKit instance with all its methods
export const appKit = {
  ...appKitInstance,
  useWallet: appKitInstance.useWallet,
  connect: async () => {
    // Check if Passport is being used before attempting to connect
    if (isUsingPassport()) {
      console.log('AppKit: Detected Passport connection, skipping AppKit connect');
      // Return a mock connection to prevent errors
      return {
        address: localStorage.getItem('passport_address') || '',
        isConnected: true,
        provider: 'passport'
      };
    }

    console.log('AppKit: No Passport connection detected, proceeding with AppKit connect');
    const connection = await appKitInstance.connect();
    // Wait for provider to be ready
    if (!appKitInstance.provider) {
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    return connection;
  },
  waitForTransaction: appKitInstance.waitForTransaction,
  provider: appKitInstance.provider
};

// Export other constants that might be needed
export const defaultChain = bsc
export { networks }

export function AppKitProvider({ children }) {
  return (
    <WagmiProvider config={wagmiAdapter.wagmiConfig}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </WagmiProvider>
  )
}
